import { useEffect, useState, useRef, useCallback } from 'react'
import { api } from '../../lib/api'
import { CATEGORY_COLORS } from '../ui/DeviceIcon'
import { getDeviceCategory } from '@shared/unifi-fingerprints'

interface TopologyNode {
  id: string
  label: string
  type: string
  level: number
  x: number
  y: number
  ip?: string | null
  status?: string | null
  model?: string | null
  icon_url?: string | null
  isClient?: boolean
  clientCount?: number
  parentId?: string | null
}

interface TopologyEdge {
  from: string
  to: string
  type: string
  port?: number
  portLabel?: string
}

interface SiteClient {
  id: string
  mac: string
  hostname: string | null
  display_name: string | null
  connection_type: string | null
  ap_mac: string | null
  ap_name: string | null
  switch_mac: string | null
  switch_port: number | null
  icon_url: string | null
  device_category: string | null
  is_online: number
}

interface Props {
  siteId: string
  onSelectDevice?: (deviceId: string) => void
}

type ViewMode = 'infrastructure' | 'clients'

const DEVICE_ICONS: Record<string, string> = {
  router: '\u2b21',
  gateway: '\u2b21',
  switch: '\u2b21',
  camera: '\u25c9',
  nvr: '\u25a3',
  vms_server: '\u25a3',
  access_point: '\u25ce',
  controller: '\u25c8',
  reader: '\u25c8',
  intercom: '\u266b',
  speaker: '\u266b',
  ups: '\u26a1',
  pdu: '\u26a1',
  other: '\u25cf',
}

function layoutNodes(nodes: TopologyNode[], _edges: TopologyEdge[]): TopologyNode[] {
  if (nodes.length === 0) return []

  const levels: Record<number, TopologyNode[]> = {}
  for (const node of nodes) {
    if (!levels[node.level]) levels[node.level] = []
    levels[node.level].push(node)
  }

  const levelKeys = Object.keys(levels).map(Number).sort((a, b) => a - b)
  const TIER_HEIGHT = 150
  const NODE_WIDTH = 110
  const CANVAS_WIDTH = 900

  for (const level of levelKeys) {
    const nodesInLevel = levels[level]
    const totalWidth = nodesInLevel.length * NODE_WIDTH
    const startX = (CANVAS_WIDTH - totalWidth) / 2 + NODE_WIDTH / 2

    nodesInLevel.forEach((node, i) => {
      node.x = startX + i * NODE_WIDTH
      node.y = 50 + level * TIER_HEIGHT
    })
  }

  return nodes
}

export function NetworkTopology({ siteId, onSelectDevice }: Props) {
  const [nodes, setNodes] = useState<TopologyNode[]>([])
  const [edges, setEdges] = useState<TopologyEdge[]>([])
  const [loading, setLoading] = useState(true)
  const [hoveredNode, setHoveredNode] = useState<string | null>(null)
  const [hoveredEdge, setHoveredEdge] = useState<string | null>(null)
  const [viewBox, setViewBox] = useState({ x: 0, y: 0, w: 900, h: 550 })
  const svgRef = useRef<SVGSVGElement>(null)
  const [dragging, setDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [viewMode, setViewMode] = useState<ViewMode>('infrastructure')
  const [infraNodes, setInfraNodes] = useState<TopologyNode[]>([])
  const [infraEdges, setInfraEdges] = useState<TopologyEdge[]>([])
  const [expandedSummaries, setExpandedSummaries] = useState<Set<string>>(new Set())
  const [summaryClients, setSummaryClients] = useState<Map<string, SiteClient[]>>(new Map())

  useEffect(() => {
    api.get<{ nodes: TopologyNode[]; edges: TopologyEdge[] }>(`/sites/${siteId}/topology`)
      .then(data => {
        const laid = layoutNodes(data.nodes, data.edges)
        setInfraNodes(laid)
        setInfraEdges(data.edges)
        setNodes(laid)
        setEdges(data.edges)

        if (laid.length > 0) {
          const maxX = Math.max(...laid.map(n => n.x)) + 80
          const maxY = Math.max(...laid.map(n => n.y)) + 100
          setViewBox({ x: -40, y: -20, w: Math.max(900, maxX), h: Math.max(450, maxY) })
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [siteId])

  // Load clients when switching to client view
  useEffect(() => {
    if (viewMode === 'infrastructure') {
      setNodes(infraNodes)
      setEdges(infraEdges)
      setExpandedSummaries(new Set())
      return
    }

    api.get<{ data: SiteClient[] }>(`/sites/${siteId}/clients?limit=500&is_online=true`)
      .then(resp => {
        const clients = resp.data || []

        const clientsByParent = new Map<string, SiteClient[]>()
        for (const cl of clients) {
          const parentMac = cl.connection_type === 'wired' ? cl.switch_mac : cl.ap_mac
          if (!parentMac) continue
          if (!clientsByParent.has(parentMac)) clientsByParent.set(parentMac, [])
          clientsByParent.get(parentMac)!.push(cl)
        }

        api.get<{ data: Array<{ id: string; mac_address: string | null }> }>(`/sites/${siteId}/devices?limit=200`)
          .then(devResp => {
            const macById = new Map<string, string>()
            for (const d of devResp.data || []) {
              if (d.mac_address) macById.set(d.id, d.mac_address)
            }

            const newNodes = [...infraNodes]
            const newEdges = [...infraEdges]
            const maxLevel = Math.max(...infraNodes.map(n => n.level), 0)
            const clientMap = new Map<string, SiteClient[]>()

            for (const infraNode of infraNodes) {
              const mac = macById.get(infraNode.id)
              if (!mac) continue
              const parentClients = clientsByParent.get(mac) || []
              if (parentClients.length === 0) continue

              const CLIENT_THRESHOLD = 20
              if (parentClients.length > CLIENT_THRESHOLD) {
                const summaryId = `summary-${infraNode.id}`
                // Store clients for later expansion
                clientMap.set(summaryId, parentClients)
                newNodes.push({
                  id: summaryId,
                  label: `${parentClients.length} clients`,
                  type: 'client_summary',
                  level: maxLevel + 1,
                  x: infraNode.x,
                  y: infraNode.y + 120,
                  isClient: true,
                  clientCount: parentClients.length,
                  parentId: infraNode.id,
                })
                newEdges.push({ from: infraNode.id, to: summaryId, type: 'wireless' })
              } else {
                const spread = Math.min(parentClients.length * 65, 450)
                const startOffset = -spread / 2
                parentClients.forEach((cl, i) => {
                  const clientNodeId = `client-${cl.mac}`
                  newNodes.push({
                    id: clientNodeId,
                    label: cl.display_name || cl.hostname || cl.mac,
                    type: cl.device_category || (cl.connection_type === 'wired' ? 'workstation' : 'phone'),
                    level: maxLevel + 1,
                    x: infraNode.x + startOffset + i * 65,
                    y: infraNode.y + 120,
                    status: cl.is_online ? 'online' : 'offline',
                    icon_url: cl.icon_url,
                    isClient: true,
                    parentId: infraNode.id,
                  })
                  newEdges.push({
                    from: infraNode.id,
                    to: clientNodeId,
                    type: cl.connection_type === 'wired' ? 'copper' : 'wireless',
                  })
                })
              }
            }

            const laid = layoutNodes(newNodes, newEdges)
            setNodes(laid)
            setEdges(newEdges)
            setSummaryClients(clientMap)

            if (laid.length > 0) {
              const maxX = Math.max(...laid.map(n => n.x)) + 80
              const maxY = Math.max(...laid.map(n => n.y)) + 100
              setViewBox({ x: -40, y: -20, w: Math.max(900, maxX), h: Math.max(450, maxY) })
            }
          })
          .catch(() => {})
      })
      .catch(() => {})
  }, [viewMode, siteId, infraNodes, infraEdges])

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault()
    const scale = e.deltaY > 0 ? 1.1 : 0.9
    setViewBox(prev => ({
      x: prev.x,
      y: prev.y,
      w: prev.w * scale,
      h: prev.h * scale,
    }))
  }, [])

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.target === svgRef.current || (e.target as SVGElement).tagName === 'svg') {
      setDragging(true)
      setDragStart({ x: e.clientX, y: e.clientY })
    }
  }, [])

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragging) return
    const dx = (e.clientX - dragStart.x) * (viewBox.w / 900)
    const dy = (e.clientY - dragStart.y) * (viewBox.h / 550)
    setViewBox(prev => ({ ...prev, x: prev.x - dx, y: prev.y - dy }))
    setDragStart({ x: e.clientX, y: e.clientY })
  }, [dragging, dragStart, viewBox])

  const handleMouseUp = useCallback(() => setDragging(false), [])

  const toggleSummary = (summaryId: string) => {
    const summaryNode = nodes.find(n => n.id === summaryId)
    if (!summaryNode) return
    const clients = summaryClients.get(summaryId)
    if (!clients) return

    setExpandedSummaries(prev => {
      const next = new Set(prev)
      const isExpanding = !next.has(summaryId)

      if (isExpanding) {
        next.add(summaryId)
        // Add individual client nodes
        const spread = Math.min(clients.length * 65, 600)
        const startOffset = -spread / 2
        const maxLevel = Math.max(...nodes.map(n => n.level), 0)
        const newClientNodes: TopologyNode[] = clients.map((cl, i) => ({
          id: `client-${cl.mac}`,
          label: cl.display_name || cl.hostname || cl.mac,
          type: cl.device_category || (cl.connection_type === 'wired' ? 'workstation' : 'phone'),
          level: maxLevel + 1,
          x: summaryNode.x + startOffset + i * 65,
          y: summaryNode.y + 80,
          status: cl.is_online ? 'online' : 'offline',
          icon_url: cl.icon_url,
          isClient: true,
          parentId: summaryNode.parentId,
        }))
        const newClientEdges: TopologyEdge[] = clients.map(cl => ({
          from: summaryId,
          to: `client-${cl.mac}`,
          type: cl.connection_type === 'wired' ? 'copper' : 'wireless',
        }))
        setNodes(prev => [...prev, ...newClientNodes])
        setEdges(prev => [...prev, ...newClientEdges])
        // Expand viewbox
        const maxX = Math.max(...[...nodes, ...newClientNodes].map(n => n.x)) + 80
        const maxY = Math.max(...[...nodes, ...newClientNodes].map(n => n.y)) + 100
        setViewBox(prev => ({ ...prev, w: Math.max(prev.w, maxX + 40), h: Math.max(prev.h, maxY + 20) }))
      } else {
        next.delete(summaryId)
        // Remove expanded client nodes
        const clientIds = new Set(clients.map(cl => `client-${cl.mac}`))
        setNodes(prev => prev.filter(n => !clientIds.has(n.id)))
        setEdges(prev => prev.filter(e => !clientIds.has(e.to)))
      }

      return next
    })
  }

  if (loading) {
    return <div className="skeleton" style={{ height: 500, borderRadius: 'var(--r-l)' }} />
  }

  if (infraNodes.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--text-3)' }}>
        <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.3 }}>{'\uD83D\uDD17'}</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)', marginBottom: 4, fontFamily: 'var(--font)' }}>No topology data</div>
        <p style={{ fontSize: 13, color: 'var(--text-3)', fontFamily: 'var(--font)' }}>
          Map device port connections to generate the network topology.
        </p>
      </div>
    )
  }

  const nodeMap = new Map(nodes.map(n => [n.id, n]))

  const highlightedEdges = hoveredNode
    ? edges.filter(e => e.from === hoveredNode || e.to === hoveredNode)
    : []
  const highlightedEdgeKeys = new Set(highlightedEdges.map(e => `${e.from}-${e.to}`))

  return (
    <div style={{ borderRadius: 'var(--r-l)', overflow: 'hidden', background: 'var(--bg-0)', border: '1px solid var(--border)' }}>
      {/* View mode toggle — segmented control */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)' }}>
        <div style={{
          display: 'inline-flex', borderRadius: 'var(--r-m)', overflow: 'hidden',
          border: '1px solid var(--border)', background: 'var(--bg-2)',
        }}>
          {(['infrastructure', 'clients'] as const).map(mode => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              style={{
                padding: '6px 16px', border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                fontFamily: 'var(--font)',
                background: viewMode === mode ? 'var(--accent)' : 'transparent',
                color: viewMode === mode ? 'var(--accent-t)' : 'var(--text-3)',
                transition: 'all 100ms ease',
              }}
            >{mode === 'infrastructure' ? 'Infrastructure' : 'All Clients'}</button>
          ))}
        </div>
        <span style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>
          {nodes.filter(n => !n.isClient).length} devices
          {viewMode === 'clients' && ` \u00B7 ${nodes.filter(n => n.isClient).length} clients`}
        </span>
      </div>

      <svg
        ref={svgRef}
        viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`}
        style={{ width: '100%', height: 550, cursor: dragging ? 'grabbing' : 'grab', display: 'block' }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <defs>
          {/* Glow filter for hovered nodes */}
          <filter id="topo-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          {/* Subtle gradient for edges */}
          <linearGradient id="edge-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.6" />
            <stop offset="100%" stopColor="var(--accent)" stopOpacity="0.15" />
          </linearGradient>
        </defs>

        {/* Edges — curved Bezier paths instead of straight lines */}
        {edges.map((edge, i) => {
          const from = nodeMap.get(edge.from)
          const to = nodeMap.get(edge.to)
          if (!from || !to) return null

          const key = `${edge.from}-${edge.to}`
          const isHighlighted = hoveredNode ? highlightedEdgeKeys.has(key) : true
          const isHovered = hoveredEdge === key
          const isClientEdge = to.isClient

          // Curved path
          const x1 = from.x
          const y1 = from.y + 30
          const x2 = to.x
          const y2 = to.y - (isClientEdge ? 16 : 30)
          const midY = (y1 + y2) / 2
          const path = `M${x1},${y1} C${x1},${midY} ${x2},${midY} ${x2},${y2}`

          const baseColor = isHighlighted
            ? (edge.type === 'fiber' ? 'var(--accent)' : edge.type === 'wireless' ? 'rgba(124,58,237,0.4)' : 'var(--text-3)')
            : 'rgba(255,255,255,0.04)'

          return (
            <g key={i}>
              <path
                d={path}
                fill="none"
                stroke={baseColor}
                strokeWidth={isHovered ? 3 : isHighlighted ? (isClientEdge ? 1 : 1.5) : 0.5}
                strokeDasharray={edge.type === 'fiber' ? '8,4' : edge.type === 'wireless' ? '3,4' : undefined}
                style={{ transition: 'all 150ms ease', cursor: 'pointer' }}
                onMouseEnter={() => setHoveredEdge(key)}
                onMouseLeave={() => setHoveredEdge(null)}
              />
              {/* Port label on hover */}
              {isHovered && !isClientEdge && (
                <g>
                  <rect
                    x={(x1 + x2) / 2 - 30} y={midY - 8}
                    width={60} height={16} rx={4}
                    fill="var(--bg-2)" stroke="var(--border)" strokeWidth={0.5}
                  />
                  <text
                    x={(x1 + x2) / 2} y={midY + 1}
                    textAnchor="middle" dominantBaseline="middle"
                    style={{ fontSize: 8, fill: 'var(--text-2)', fontWeight: 600, fontFamily: 'var(--mono)' }}
                  >
                    {edge.type === 'fiber' ? 'Fiber' : 'Copper'}
                    {edge.portLabel && ` ${edge.portLabel}`}
                  </text>
                </g>
              )}
            </g>
          )
        })}

        {/* Nodes */}
        {nodes.map(node => {
          // ─── Client summary node (clickable to expand) ───
          if (node.type === 'client_summary') {
            const isExpanded = expandedSummaries.has(node.id)
            const isHovered = hoveredNode === node.id
            return (
              <g
                key={node.id}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHoveredNode(node.id)}
                onMouseLeave={() => setHoveredNode(null)}
                onClick={() => toggleSummary(node.id)}
              >
                {/* Pill background */}
                <rect
                  x={node.x - 48} y={node.y - 14}
                  width={96} height={28}
                  rx={14}
                  fill={isHovered ? 'var(--accent-m)' : 'var(--bg-3)'}
                  stroke={isHovered ? 'var(--accent)' : 'var(--border)'}
                  strokeWidth={1}
                  style={{ transition: 'all 150ms ease' }}
                />
                {/* Icon */}
                <text
                  x={node.x - 28} y={node.y + 1}
                  textAnchor="middle" dominantBaseline="middle"
                  style={{ fontSize: 11, fill: isHovered ? 'var(--accent)' : 'var(--text-3)' }}
                >
                  {isExpanded ? '\u25BC' : '\u25B6'}
                </text>
                {/* Label */}
                <text
                  x={node.x + 4} y={node.y + 1}
                  textAnchor="middle" dominantBaseline="middle"
                  style={{
                    fontSize: 11, fontWeight: 600,
                    fill: isHovered ? 'var(--accent)' : 'var(--text-2)',
                    fontFamily: 'var(--mono)', pointerEvents: 'none',
                  }}
                >
                  {node.label}
                </text>
              </g>
            )
          }

          // ─── Client node ───
          if (node.isClient) {
            const category = getDeviceCategory(node.type, null)
            const color = CATEGORY_COLORS[category] || 'var(--text-3)'
            const isOnline = node.status === 'online'
            const nodeSize = 30
            const isHovered = hoveredNode === node.id
            return (
              <g
                key={node.id}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHoveredNode(node.id)}
                onMouseLeave={() => setHoveredNode(null)}
              >
                {node.icon_url ? (
                  <>
                    <rect
                      x={node.x - nodeSize / 2} y={node.y - nodeSize / 2}
                      width={nodeSize} height={nodeSize}
                      rx={6} fill="var(--bg-2)"
                      stroke={isHovered ? color : 'var(--border)'}
                      strokeWidth={isHovered ? 1.5 : 0.5}
                      style={{ transition: 'all 150ms ease' }}
                    />
                    <image
                      href={node.icon_url}
                      x={node.x - nodeSize / 2 + 3} y={node.y - nodeSize / 2 + 3}
                      width={nodeSize - 6} height={nodeSize - 6}
                      preserveAspectRatio="xMidYMid meet"
                      style={{ pointerEvents: 'none' }}
                    />
                  </>
                ) : (
                  <circle
                    cx={node.x} cy={node.y} r={nodeSize / 2 - 2}
                    fill={isHovered ? `${color}20` : 'var(--bg-2)'}
                    stroke={isHovered ? color : 'var(--border)'}
                    strokeWidth={isHovered ? 1.5 : 0.5}
                    style={{ transition: 'all 150ms ease' }}
                  />
                )}
                {/* Status dot */}
                <circle
                  cx={node.x + nodeSize / 2 - 3} cy={node.y - nodeSize / 2 + 3}
                  r={3}
                  fill={isOnline ? 'var(--ok)' : 'var(--err)'}
                  stroke="var(--bg-0)" strokeWidth={1.5}
                />
                {/* Label */}
                <text
                  x={node.x} y={node.y + nodeSize / 2 + 10}
                  textAnchor="middle"
                  style={{ fontSize: 8, fill: 'var(--text-3)', fontFamily: 'var(--mono)', pointerEvents: 'none' }}
                >
                  {node.label.length > 14 ? node.label.slice(0, 14) + '\u2026' : node.label}
                </text>
              </g>
            )
          }

          // ─── Infrastructure node ───
          const category = getDeviceCategory(node.type, node.model || null)
          const color = CATEGORY_COLORS[category] || 'var(--text-3)'
          const icon = DEVICE_ICONS[node.type] || DEVICE_ICONS.other
          const isHovered = hoveredNode === node.id
          const isConnected = hoveredNode ? highlightedEdges.some(e => e.from === node.id || e.to === node.id) : false
          const isOnline = node.status === 'online'
          const hasImage = !!node.icon_url
          const nodeSize = 52

          return (
            <g
              key={node.id}
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setHoveredNode(node.id)}
              onMouseLeave={() => setHoveredNode(null)}
              onClick={() => onSelectDevice?.(node.id)}
              filter={isHovered ? 'url(#topo-glow)' : undefined}
            >
              {hasImage ? (
                <>
                  {/* Card background */}
                  <rect
                    x={node.x - nodeSize / 2 - 4} y={node.y - nodeSize / 2 - 4}
                    width={nodeSize + 8} height={nodeSize + 8}
                    rx={12}
                    fill="var(--bg-2)"
                    stroke={isHovered || isConnected ? color : 'var(--border)'}
                    strokeWidth={isHovered ? 2 : 1}
                    style={{ transition: 'all 150ms ease' }}
                  />
                  <image
                    href={node.icon_url!}
                    x={node.x - nodeSize / 2 + 2} y={node.y - nodeSize / 2 + 2}
                    width={nodeSize - 4} height={nodeSize - 4}
                    preserveAspectRatio="xMidYMid meet"
                    style={{ pointerEvents: 'none' }}
                  />
                </>
              ) : (
                <>
                  {/* Circle node with color ring */}
                  <circle
                    cx={node.x} cy={node.y} r={nodeSize / 2}
                    fill="var(--bg-2)"
                    stroke={isHovered || isConnected ? color : `${color}30`}
                    strokeWidth={isHovered ? 2.5 : 1.5}
                    style={{ transition: 'all 150ms ease' }}
                  />
                  <text
                    x={node.x} y={node.y + 2}
                    textAnchor="middle" dominantBaseline="middle"
                    style={{ fontSize: 20, fill: color, pointerEvents: 'none' }}
                  >
                    {icon}
                  </text>
                </>
              )}
              {/* Status ring */}
              {node.status && (
                <circle
                  cx={node.x + (hasImage ? nodeSize / 2 : nodeSize / 2 - 2)}
                  cy={node.y - (hasImage ? nodeSize / 2 : nodeSize / 2 - 2)}
                  r={5}
                  fill={isOnline ? 'var(--ok)' : 'var(--err)'}
                  stroke="var(--bg-0)"
                  strokeWidth={2}
                />
              )}
              {/* Label */}
              <text
                x={node.x} y={node.y + (hasImage ? nodeSize / 2 + 8 : nodeSize / 2 + 14)}
                textAnchor="middle"
                style={{
                  fontSize: 10, fontWeight: isHovered ? 600 : 500,
                  fill: isHovered ? 'var(--text-1)' : 'var(--text-2)',
                  fontFamily: 'var(--font)',
                  pointerEvents: 'none',
                  transition: 'all 150ms ease',
                }}
              >
                {node.label.length > 16 ? node.label.slice(0, 16) + '\u2026' : node.label}
              </text>
              {/* Model subtitle */}
              {node.model && (
                <text
                  x={node.x} y={node.y + (hasImage ? nodeSize / 2 + 20 : nodeSize / 2 + 26)}
                  textAnchor="middle"
                  style={{ fontSize: 8, fill: 'var(--text-3)', fontFamily: 'var(--mono)', pointerEvents: 'none' }}
                >
                  {node.model}
                </text>
              )}
              {/* IP tooltip on hover */}
              {isHovered && node.ip && (
                <g>
                  <rect
                    x={node.x - 40} y={node.y + (hasImage ? nodeSize / 2 + 26 : nodeSize / 2 + 32)}
                    width={80} height={16} rx={4}
                    fill="var(--bg-3)" stroke="var(--border)" strokeWidth={0.5}
                  />
                  <text
                    x={node.x} y={node.y + (hasImage ? nodeSize / 2 + 35 : nodeSize / 2 + 41)}
                    textAnchor="middle" dominantBaseline="middle"
                    style={{ fontSize: 9, fill: color, fontFamily: 'var(--mono)', fontWeight: 600, pointerEvents: 'none' }}
                  >
                    {node.ip}
                  </text>
                </g>
              )}
            </g>
          )
        })}
      </svg>
    </div>
  )
}
